/**
 * Simulation inactivity monitor hook.
 *
 * Tracks learner interaction during active simulation runtime and opens
 * a presence prompt after the configured inactivity period.
 *
 * @module useSimulationInactivityMonitor
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const ACTIVITY_EVENTS: ReadonlyArray<keyof WindowEventMap> = [
  'pointerdown',
  'keydown',
  'scroll',
  'touchstart',
];

interface UseSimulationInactivityMonitorOptions {
  readonly enabled: boolean;
  readonly timeoutMs: number;
}

interface UseSimulationInactivityMonitorReturn {
  readonly isPromptOpen: boolean;
  readonly continueSession: () => void;
}

export function useSimulationInactivityMonitor({
  enabled,
  timeoutMs,
}: UseSimulationInactivityMonitorOptions): UseSimulationInactivityMonitorReturn {
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const lastActivityAtRef = useRef<number>(Date.now());
  const timeoutRef = useRef<number>(Math.max(1, timeoutMs));
  const promptOpenRef = useRef<boolean>(false);

  useEffect(() => {
    timeoutRef.current = Math.max(1, timeoutMs);
  }, [timeoutMs]);

  useEffect(() => {
    promptOpenRef.current = isPromptOpen;
  }, [isPromptOpen]);

  const markActivity = useCallback(() => {
    if (!enabled || promptOpenRef.current) {
      return;
    }
    lastActivityAtRef.current = Date.now();
  }, [enabled]);

  const continueSession = useCallback(() => {
    lastActivityAtRef.current = Date.now();
    setIsPromptOpen(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setIsPromptOpen(false);
      return;
    }

    lastActivityAtRef.current = Date.now();
    setIsPromptOpen(false);

    const intervalId = globalThis.setInterval(() => {
      if (promptOpenRef.current) {
        return;
      }
      const idleMs = Date.now() - lastActivityAtRef.current;
      if (idleMs >= timeoutRef.current) {
        setIsPromptOpen(true);
      }
    }, 1000);

    ACTIVITY_EVENTS.forEach((eventName) => {
      globalThis.addEventListener(eventName, markActivity, { passive: true });
    });

    return () => {
      globalThis.clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((eventName) => {
        globalThis.removeEventListener(eventName, markActivity);
      });
    };
  }, [enabled, markActivity]);

  return {
    isPromptOpen,
    continueSession,
  };
}
